<script setup lang="ts">
import type { RelatedCompany } from '~~/server/types/company'
import { proxyImageUrl } from '~/utils/imageProxy'

const props = defineProps<{
  company: RelatedCompany
  section: string
}>()
</script>

<template>
  <NuxtLink
    :to="`/company/${company.permalink}${
      section !== 'overview' ? `/${section}` : ''
    }`"
    class="border border-gray-100 rounded-xl p-0 flex flex-row md:flex-col h-auto md:h-[250px] hover:shadow-lg transition-all duration-300 w-full hover:border-primary/30 bg-white group"
  >
    <div
      class="w-[100px] h-[100px] md:w-auto md:h-auto bg-white flex items-center justify-center border-r md:border-r-0 md:mb-5 border-solid border-gray-100 flex-shrink-0 rounded-l-xl md:rounded-l-none overflow-hidden md:mt-6"
    >
      <div
        class="w-[70px] h-[70px] md:w-24 md:h-24 bg-gray-50 rounded-lg p-2 flex items-center justify-center group-hover:bg-gray-100/80 transition-colors"
      >
        <img
          v-if="company.logo_url"
          :src="proxyImageUrl(company.logo_url)"
          :alt="`${company.brand_name} logo`"
          width="50"
          height="50"
          class="max-h-[50px] max-w-[50px] md:max-h-16 md:max-w-16 w-auto h-auto object-contain"
          loading="lazy"
          decoding="async"
          @error="($event.target as HTMLImageElement).src = '/img/logo.svg'"
        >
        <div
          v-else
          class="h-16 w-16 bg-gray-100 rounded-full flex items-center justify-center"
        >
          <span class="text-lg font-semibold text-gray-400">{{
            company.brand_name.charAt(0)
          }}</span>
        </div>
      </div>
    </div>
    <div class="flex-1 px-4 py-3 md:p-0 flex flex-col md:items-center justify-between">
      <h3 class="font-medium md:font-semibold text-gray-800 md:text-lg line-clamp-1 md:line-clamp-2 md:text-center group-hover:text-primary transition-colors">
        {{ company.brand_name }}
      </h3>
      <div class="flex items-center mt-1.5 md:mt-3 md:mb-4 md:justify-center">
        <div
          class="flex items-center px-2 py-0.5 md:px-3 md:py-1 rounded-md bg-primary/10 text-primary-700"
        >
          <span class="text-sm md:text-base font-semibold mr-1.5 md:mr-2">
            {{ company.overall_rating.toFixed(1) }}
          </span>
          <RatingStars
            v-if="company.overall_rating"
            :rating="company.overall_rating"
            class="text-primary-700"
          />
          <span
            v-else
            class="text-sm text-gray-700"
          >No ratings</span>
        </div>
      </div>
    </div>
  </NuxtLink>
</template>
